import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailLabelLanguage,
  InsertHrailLabelLanguageBody,
  UpdateHrailLabelLanguageBody,
} from "./types/labelLanguages";

export const useHrailLabelLanguagesApi = () => {
  const api = useApi();

  const getAdminHrailLabelLanguage = useCallback(
    (labelLanguageId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/label_languages/${labelLanguageId}`;
      return useSWRMutation<HrailLabelLanguage, CustomError, string>(url, (url) =>
        api.get<HrailLabelLanguage>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailLabelLanguages = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/label_languages`, query);
      return useSWRImmutable<Paginated<HrailLabelLanguage>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailLabelLanguage>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailLabelLanguages = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/label_languages`, query);
      return api.get<Paginated<HrailLabelLanguage>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailLabelLanguage = useCallback(
    async (body: InsertHrailLabelLanguageBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailLabelLanguageBody>(
        `/admin/settings/hrail/label_languages`,
        body,
      );
      mutateCache([`/admin/settings/hrail/label_languages`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailLabelLanguage = useCallback(
    async (labelLanguageId: string, body: UpdateHrailLabelLanguageBody): Promise<void> => {
      const url = `/admin/settings/hrail/label_languages/${labelLanguageId}`;
      await api.put<UpdateHrailLabelLanguageBody>(url, body);
      mutateCache([`/admin/settings/hrail/label_languages`], url);
    },
    [api],
  );

  const updateAdminHrailLabelLanguageVisibility = useCallback(
    async (labelLanguageId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/label_languages/${labelLanguageId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/label_languages`]);
    },
    [api],
  );

  const updateAdminHrailLabelLanguageOrder = useCallback(
    async (labelLanguageId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/label_languages/${labelLanguageId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/label_languages`]);
    },
    [api],
  );

  const deleteAdminHrailLabelLanguage = useCallback(
    async (labelLanguageId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/label_languages/${labelLanguageId}`);
      mutateCache([`/admin/settings/hrail/label_languages`]);
    },
    [api],
  );

  return {
    getAdminHrailLabelLanguage,
    useAdminHrailLabelLanguages,
    getAdminHrailLabelLanguages,
    insertAdminHrailLabelLanguage,
    updateAdminHrailLabelLanguage,
    updateAdminHrailLabelLanguageVisibility,
    updateAdminHrailLabelLanguageOrder,
    deleteAdminHrailLabelLanguage,
  };
};
